import { usePrevious } from '@timelyapp/react';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import React, { useCallback, useEffect, useState } from 'react';
import { HexColorPicker as ColorfulHexColorPicker } from 'react-colorful';
import { expandShorthandHexColor, stripHexPrefix } from 'utils/colors';
import styles from './HexColorPicker.module.scss';
import Input from './Input';
import InputDecorator from './InputDecorator';
import Popover from './popovers/Popover';

interface HexColorPickerProps {
  color?: string;
  setColor: (color: string) => void;
  disabled?: boolean;
}

const HexColorPicker = React.memo<HexColorPickerProps>(({ color = '', setColor, disabled }) => {
  const [hex, setHex] = useState(stripHexPrefix(color));

  const previousColor = usePrevious(color);
  useEffect(() => {
    const expandedHexState = expandShorthandHexColor(hex);
    if (color !== previousColor && color !== expandedHexState) {
      setHex(color);
    }
  }, [color, previousColor, hex]);
  const handleChangeHex = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = stripHexPrefix(event.target.value);
      setHex(value);
      const expandedHex = expandShorthandHexColor(value);
      if (expandedHex.length === 6) {
        setColor(expandedHex);
      }
    },
    [setColor]
  );
  const handleHexInputBlur = useCallback(() => {
    setHex(color);
  }, [color]);
  const handleSetHexFromPicker = useCallback(
    (color: string) => {
      setHex(stripHexPrefix(color));
      setColor(stripHexPrefix(color));
    },
    [setColor]
  );

  const [isColorfulOpen, setIsColorfulOpen] = useState(false);
  const handleHexInputFocus = useCallback(() => {
    setIsColorfulOpen(true);
  }, [setIsColorfulOpen]);
  const handleClickOutsideColorfulAndHex = useCallback(() => {
    setIsColorfulOpen(false);
  }, []);

  const clickOutsideManualInputRef = useOnClickOutside<HTMLDivElement>(
    handleClickOutsideColorfulAndHex
  );

  return (
    <div ref={clickOutsideManualInputRef}>
      <InputDecorator prefix="#">
        <Input
          className={styles.hexInput}
          type="text"
          required
          maxLength={6}
          name="hexColor"
          value={hex}
          onChange={handleChangeHex}
          onFocus={handleHexInputFocus}
          onBlur={handleHexInputBlur}
          disabled={disabled}
        />
      </InputDecorator>
      <Popover
        enableKeyTrap={false}
        arrow={false}
        position="bottom-start"
        isOpen={isColorfulOpen}
        target={<div />}
      >
        <div
          className={styles.colorful}
          onMouseDown={e => {
            e.preventDefault();
          }}
        >
          <ColorfulHexColorPicker color={`#${color}`} onChange={handleSetHexFromPicker} />
        </div>
      </Popover>
    </div>
  );
});

HexColorPicker.displayName = 'HexColorPicker';
export default HexColorPicker;
